import React from "react";
import "./InfoCards.css";
import { SkeletonBar } from "../../../components/Skeleton/Skeleton";

/*
  Shared, EAGERLY-imported skeletons for the Information / Expenses screens.

  Two flavours per screen:
    - *CardsSkeleton  : the content grid only (used INSIDE the mounted screen
                        while its data loads).
    - *PageSkeleton   : full page (header + content), used as the lazy-route
                        Suspense fallback so the brief chunk-download shows a
                        page-specific placeholder instead of the generic
                        <Loader/> default skeleton.

  Because navigation/index.tsx imports the *PageSkeletons eagerly, InfoCards.css
  lands in the main bundle and the fallback renders fully styled.
*/

const HeaderSkeleton = ({ actions = 2 }: { actions?: number }) => (
  <div className="ic-head">
    <div className="ic-headings">
      <span className="cd-skl" style={{ width: 190, height: 24, borderRadius: 7 }} />
      <span className="cd-skl" style={{ width: 130, height: 11, borderRadius: 6, marginTop: 7 }} />
    </div>
    <div className="ic-actions">
      {Array.from({ length: actions }).map((_, i) => (
        <span className="cd-skl" key={i} style={{ width: 96, height: 36, borderRadius: 9 }} />
      ))}
    </div>
  </div>
);

/* back-button + title header (archived sub-pages) */
const BackHeaderSkeleton = () => (
  <div className="ic-head">
    <span className="cd-skl" style={{ width: 36, height: 36, borderRadius: 9 }} />
    <div className="ic-headings">
      <span className="cd-skl" style={{ width: 180, height: 24, borderRadius: 7 }} />
      <span className="cd-skl" style={{ width: 120, height: 11, borderRadius: 6, marginTop: 7 }} />
    </div>
  </div>
);

/* ---------- Health & Lifestyle ---------- */
export const HealthCardsSkeleton = () => (
  <div className="ic-cards">
    {Array.from({ length: 6 }).map((_, i) => (
      <div className="ic-skl-card" key={i}>
        <div className="ic-skl-head">
          <span className="cd-skl" style={{ width: 40, height: 40, borderRadius: 999 }} />
          <SkeletonBar w="55%" h={14} />
        </div>
        <div className="ic-skl-body" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
          {Array.from({ length: 3 }).map((_, j) => (
            <span className="cd-skl" key={j} style={{ height: 78, borderRadius: 12 }} />
          ))}
        </div>
      </div>
    ))}
  </div>
);
export const HealthPageSkeleton = () => (
  <div className="page-content-wraper icpage"><HeaderSkeleton actions={2} /><HealthCardsSkeleton /></div>
);

/* ---------- Addresses ---------- */
export const AddressCardsSkeleton = () => (
  <div className="ic-cards cols-2">
    {Array.from({ length: 4 }).map((_, i) => (
      <div className="ic-skl-card" key={i}>
        <div className="ic-skl-head">
          <span className="cd-skl" style={{ width: 36, height: 36, borderRadius: 10 }} />
          <SkeletonBar w="45%" h={14} />
        </div>
        <div className="ic-skl-body">
          {Array.from({ length: 3 }).map((_, j) => (
            <div key={j} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
              <SkeletonBar w={70 + (j % 2) * 20} h={11} />
              <SkeletonBar w={120} h={12} />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);
export const AddressPageSkeleton = () => (
  <div className="page-content-wraper icpage"><HeaderSkeleton actions={3} /><AddressCardsSkeleton /></div>
);

/* ---------- Income ---------- */
export const IncomeGroupsSkeleton = () => (
  <div className="ic-group">
    <span className="cd-skl" style={{ display: "inline-block", width: 230, height: 64, borderRadius: 14, marginBottom: 14 }} />
    <div className="ic-cards">
      {Array.from({ length: 3 }).map((_, i) => (
        <div className="ic-skl-card" key={i}>
          <div className="ic-skl-head">
            <span className="cd-skl" style={{ width: 36, height: 36, borderRadius: 10 }} />
            <SkeletonBar w="55%" h={14} />
          </div>
          <div className="ic-skl-body">
            {Array.from({ length: 3 }).map((_, j) => (
              <div key={j} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
                <SkeletonBar w={80} h={11} />
                <SkeletonBar w={90} h={12} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);
export const IncomePageSkeleton = () => (
  <div className="page-content-wraper icpage"><HeaderSkeleton actions={4} /><IncomeGroupsSkeleton /></div>
);

/* ---------- Archived Address ---------- */
export const ArchivedAddressPageSkeleton = () => (
  <div className="page-content-wraper icpage"><BackHeaderSkeleton /><AddressCardsSkeleton /></div>
);

/* ---------- Archived Income ---------- */
export const ArchivedIncomeCardsSkeleton = () => (
  <div className="ic-cards">
    {Array.from({ length: 6 }).map((_, i) => (
      <div className="ic-skl-card" key={i}>
        <div className="ic-skl-head">
          <span className="cd-skl" style={{ width: 36, height: 36, borderRadius: 10 }} />
          <div style={{ flex: 1 }}>
            <SkeletonBar w="55%" h={14} />
            <SkeletonBar w="35%" h={10} style={{ marginTop: 6 }} />
          </div>
        </div>
        <div className="ic-skl-body">
          {Array.from({ length: 3 }).map((_, j) => (
            <div key={j} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
              <SkeletonBar w={80} h={11} />
              <SkeletonBar w={90} h={12} />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);
export const ArchivedIncomePageSkeleton = () => (
  <div className="page-content-wraper icpage"><BackHeaderSkeleton /><ArchivedIncomeCardsSkeleton /></div>
);

/* ---------- Expenses (plan selection) ---------- */
export const ExpensesPageSkeleton = () => (
  <div className="page-content-wraper icpage">
    <HeaderSkeleton actions={0} />
    <div className="ic-plans">
      {Array.from({ length: 3 }).map((_, i) => (
        <div className="ic-skl-card" key={i} style={{ minHeight: 138, padding: "15px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 9 }}>
            <span className="cd-skl" style={{ width: 20, height: 20, borderRadius: 999 }} />
            <SkeletonBar w="45%" h={16} />
          </div>
          <SkeletonBar w="92%" h={11} />
          <SkeletonBar w="78%" h={11} style={{ marginTop: 9 }} />
        </div>
      ))}
    </div>
    <div className="ic-continue-wrap">
      <SkeletonBar w={180} h={42} r={9} />
      <SkeletonBar w={180} h={42} r={9} />
    </div>
  </div>
);
